import { isReadOnlyViewer } from '../../utils/hackathonStatus';
import React, { useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { useHackathon } from '../../hooks/useHackathon';
import { JoinHackathon } from '../../components/Invite/JoinHackathon';
import { Loading } from '../../components/Loading';
import { useAuth } from '../../services/useAuth';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import GavelIcon from '@mui/icons-material/Gavel';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import GroupsIcon from '@mui/icons-material/Groups';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import { useSessions } from '../../hooks/useSessions';
import { useLiveBoard } from '../../hooks/useLiveSessions';
import { useHackathonSync } from '../../hooks/useHackathonSync';
import { SessionsCard } from '../../components/Sessions/SessionsCard';
import { LiveNowStrip } from '../../components/Sessions/LiveNowStrip';
import { IdeasList } from '../../components/Idea/IdeasList';
import { login } from '../../services/auth.service';
import { LiveVotingBanner } from '../../components/Voting/LiveVotingBanner';
import { ParticipantVotingNotice } from '../../components/Voting/ParticipantVotingNotice';
import { PageContainer } from '../../components/PageContainer';
import Stack from '@mui/material/Stack/Stack';
import Alert from '@mui/material/Alert/Alert';
import Button from '@mui/material/Button/Button';
import Typography from '@mui/material/Typography/Typography';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import Chip from '@mui/material/Chip/Chip';
import Avatar from '@mui/material/Avatar/Avatar';
import Divider from '@mui/material/Divider/Divider';
import Box from '@mui/material/Box/Box';
import { AppBreadcrumbs, PageType } from '../../components/Breadcrumbs/Buttons';
import { ScrollTopButton } from '../../components/ScrollTopButton';
import Markdown from 'react-markdown';
import { HackathonHero } from '../../components/Hackathon/HackathonHero';
import { HackathonBanner, TeamPrompt } from '../../components/Hackathon/HackathonBanner';
import { formatDateRange, getHackathonStatus, ideaDeadlineEnd, ideasOpen as ideasAccepted } from '../../utils/hackathonStatus';
import { ResultsCard } from '../../components/Hackathon/ResultsCard';
import { gateBrandingAtom } from '../../components/Theme/AppThemeProvider';
import { HackathonPayload, joinHackathon } from '../../services/hackathon.service';
import { emailMatchesDomain } from '../../utils/domain';
import { HelpLink } from '../../components/HelpLink';

const HackathonGate = ({
    hackathonId,
    data,
    authenticated
}: {
    hackathonId: string,
    data: any,
    authenticated: boolean
}) => {
    const { user } = useAuth();
    const setGateBranding = useSetAtom(gateBrandingAtom);

    const needsInviteCode = data?.needsInviteCode || false;
    const hackathonName = data?.hackathonName || 'Hackathon';
    const hackathonDomain = data?.hackathonDomain || '';
    const preferredAuth = data?.preferredAuth || '';
    // opened from a judge invite link
    const judgeLink = !!new URLSearchParams(window.location.search).get('judge');

    useEffect(() => {
        if (data?.branding) {
            setGateBranding(current => ({ ...current, [hackathonId]: data.branding }));
        }
    }, [hackathonId, data?.branding, setGateBranding]);

    const matchDomain = !hackathonDomain || (!!user && emailMatchesDomain(user.email, hackathonDomain));

    return (
        <PageContainer variant="plain">
            <Stack spacing={3} sx={{ maxWidth: 720, mx: 'auto' }}>
                <HackathonHero name={hackathonName} compact />
                <Card>
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack spacing={2}>
                            {
                                !authenticated && (
                                    <>
                                        <Typography variant="h5">You're invited</Typography>
                                        <Typography color="text.secondary">
                                            Sign in {hackathonDomain ? `with your ${hackathonDomain} email ` : ''}to see ideas, join a team and vote.
                                        </Typography>
                                        <Box>
                                            <Button variant="contained" size="large" onClick={() => login(window.location.pathname + window.location.search, preferredAuth)}>Log in or sign up</Button>
                                        </Box>
                                    </>
                                )
                            }
                            {
                                authenticated && !matchDomain && (
                                    <Alert severity='error'>
                                        This hackathon is restricted to {hackathonDomain} emails. Please sign out and sign in with a {hackathonDomain} email.
                                    </Alert>
                                )
                            }
                            {
                                authenticated && matchDomain && (
                                    <>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                                            <Typography variant="h5">{judgeLink ? 'Judge this hackathon' : 'Join this hackathon'}</Typography>
                                            {!judgeLink && <HelpLink guide="join">How joining works</HelpLink>}
                                        </Stack>
                                        <JoinHackathon
                                            hackathonId={hackathonId}
                                            needsInviteCode={needsInviteCode} />
                                    </>
                                )
                            }
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
        </PageContainer>
    )
}

const PersonChip = ({ person, showEmail }: { person: { username: string, email: string }, showEmail: boolean }) => {
    const chip = <Chip
        avatar={<Avatar>{(person.username || person.email || '?').charAt(0).toUpperCase()}</Avatar>}
        label={person.username || person.email}
        variant="outlined" />;
    return showEmail ? <Tooltip title={person.email}>{chip}</Tooltip> : chip;
}

export const RulesCard = ({ hackathon, isOrganizer }: { hackathon: HackathonPayload, isOrganizer: boolean }) => {
    return (
        <Card>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack spacing={2.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <HowToVoteIcon color="primary" />
                        <Typography variant="h5" component="h2">How it works</Typography>
                    </Stack>

                    <Stack spacing={1}>
                        <Typography variant="overline" color="text.secondary">Teams</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <GroupsIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                                {hackathon.minTeamSize === hackathon.maxTeamSize
                                    ? `${hackathon.maxTeamSize} people per team`
                                    : `${hackathon.minTeamSize}-${hackathon.maxTeamSize} people per team`}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Stack spacing={1}>
                        <Typography variant="overline" color="text.secondary">Voting</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip size="small" label={hackathon.participantScores ? 'Participants vote (anonymously)' : 'Judges vote'} />
                            <Chip size="small" label={hackathon.anonymousJudgeScores ? 'Judge scores anonymous' : 'Judge scores attributed'} />
                            {hackathon.blindJudgeScores && <Chip size="small" label="Blind judging" />}
                        </Stack>
                    </Stack>

                    {
                        hackathon.categoryMeta.length > 0 && (
                            <Stack spacing={1}>
                                <Typography variant="overline" color="text.secondary">Categories</Typography>
                                <Stack spacing={1}>
                                    {
                                        hackathon.categories.map((category, index) => (
                                            <Box key={category}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="subtitle2">{category}</Typography>
                                                    {hackathon.categoryMeta[index]?.judgeOnly && <Chip size="small" variant="outlined" label="Judges only" />}
                                                </Stack>
                                                {
                                                    hackathon.categoryMeta[index]?.description && (
                                                        <Typography variant="body2" color="text.secondary">{hackathon.categoryMeta[index].description}</Typography>
                                                    )
                                                }
                                            </Box>
                                        ))
                                    }
                                </Stack>
                            </Stack>
                        )
                    }

                    {
                        hackathon.judges.length > 0 && (
                            <Stack spacing={1}>
                                <Divider />
                                <Typography variant="overline" color="text.secondary">Judge{hackathon.judges.length > 1 ? 's' : ''}</Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {
                                        hackathon.judges.map(judge => (
                                            <PersonChip key={judge.email} person={judge} showEmail={isOrganizer} />
                                        ))
                                    }
                                </Stack>
                            </Stack>
                        )
                    }

                    <Stack spacing={1}>
                        <Typography variant="overline" color="text.secondary">Organized by</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {
                                hackathon.organizers.map(organizer => (
                                    <PersonChip key={organizer.email} person={organizer} showEmail={isOrganizer} />
                                ))
                            }
                        </Stack>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    )
}

export const HackathonPage = () => {
    const { id } = useParams();

    const { hackathon, loading, error, reload } = useHackathon(id);

    const { user } = useAuth();

    const navigate = useNavigate();

    const { sessions } = useSessions(id);
    // polled, so the phase follows the organizer's room and voting switches without a reload
    const board = useLiveBoard(id);
    // ...and the ideas, teams and people follow too, refetched when the board says they changed
    useHackathonSync(id, board.version, hackathon?.participants.length || 0);

    // opened from a judge invite link while already in the hackathon
    const judgeInvite = new URLSearchParams(window.location.search).get('judge') || '';
    const [acceptingJudge, setAcceptingJudge] = React.useState(false);
    const [judgeInviteError, setJudgeInviteError] = React.useState('');

    const isOrganizer = useMemo(() => {
        if (!user || !hackathon) {
            return false;
        }

        return user && hackathon.organizers.some(o => o.email === user.email)
    }, [user, hackathon])

    const isJudge = useMemo(() => {
        if (!user || !hackathon) {
            return false;
        }
        return user && hackathon.judges.some(j => j.email === user.email)
    }, [user, hackathon])

    if (!id) {
        return <div>No id provided</div>
    }

    if (!hackathon && loading) {
        return <Loading />
    }


    if (error) {
        console.error(error);
        if (error.response?.status === 403 || error.response?.status === 401) {
            return <HackathonGate
                hackathonId={id}
                data={error.response?.data}
                authenticated={error.response?.status === 403} />
        }

        return <PageContainer>
            <Stack spacing={2}>
                <h1>Error</h1>
                <p>Why not create your own hackathon? <Link to="/new">Create</Link></p>
            </Stack>
        </PageContainer >
    }


    if (!hackathon) {
        return <PageContainer>
            <Stack spacing={2}>
                <h1>Hackathon not found</h1>
            </Stack>
        </PageContainer>
    }

    // every hackathon has a room, so an empty board just hasn't loaded yet
    const boardLoaded = board.sessions.length > 0;
    const votingNow = boardLoaded ? board.votingPeriodOn : hackathon.votingPeriodOn;
    const resultsNow = boardLoaded ? !!board.resultsAnnounced : hackathon.winners.length > 0;
    // the phase and team locks follow the board at once; the rest catches up with the refetch
    const live = { ...hackathon, votingPeriodOn: votingNow, resultsAnnounced: resultsNow };
    const presentingNow = board.sessions.some(s => !!s.activeIdea);
    const status = getHackathonStatus(live, new Date(), presentingNow, board.sessions.some(s => s.presented.length > 0));

    const actionItems = [
        isOrganizer && { title: 'Edit', icon: <EditIcon />, path: 'edit' },
        isOrganizer && { title: 'Organize', icon: <PeopleIcon />, path: 'organizer' },
        (isJudge || isOrganizer) && { title: 'Judging', icon: <GavelIcon />, path: 'judge' },
        // one room: straight to its run-of-show (organizers and its hosts); several: manage them
        sessions.length === 1 && (isOrganizer || (!!user && sessions[0].hosts.some(h => h.email === user.email)))
            && { title: 'Run presentations', icon: <SlideshowIcon />, path: `s/${sessions[0].slug}` },
        sessions.length > 1 && isOrganizer && { title: 'Sessions', icon: <MeetingRoomIcon />, path: 'sessions' }
    ].filter(Boolean) as Array<{ title: string, icon: React.ReactElement, path: string }>;

    const deadline = hackathon.ideaSubmissionDeadline;

    const scrollToId = (elementId: string) => document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth' });
    const myIdea = user ? hackathon.ideas.find(i => !i.archived && i.members.some(m => m.email === user.email)) : undefined;
    const openIdeas = hackathon.ideas.filter(i => !i.archived && !i.disqualified && i.members.length < hackathon.maxTeamSize).length;
    const ideasOpen = !!deadline && ideasAccepted(live);
    let teamPrompt: TeamPrompt | null = null;
    if (myIdea) {
        teamPrompt = { title: "You're on a team", subtitle: myIdea.title, actionLabel: 'View', onAction: () => scrollToId(myIdea._id) };
    } else if (!isOrganizer && !isJudge && openIdeas > 0) {
        teamPrompt = { title: "You're not on a team yet", subtitle: `${openIdeas} idea${openIdeas === 1 ? ' is' : 's are'} looking for people`, actionLabel: 'Browse', onAction: () => scrollToId('ideas') };
    } else if (ideasOpen && openIdeas === 0) {
        teamPrompt = { title: 'No ideas yet', subtitle: 'Be the first to pitch one', actionLabel: 'Pitch', onAction: () => scrollToId('new-idea-form') };
    }

    return (
        <PageContainer variant="plain">
            <Stack spacing={3}>
                <LiveVotingBanner hackathonId={id} />

                <AppBreadcrumbs crumbs={[
                    { type: PageType.Home, name: 'Home', link: '/' },
                    { type: PageType.Hackathon, name: hackathon.name }
                ]} />

                <HackathonBanner
                    name={hackathon.name}
                    organizationName={hackathon.resolvedBranding.displayName || hackathon.organization?.name}
                    status={status}
                    dateRange={formatDateRange(hackathon.startDate, hackathon.endDate)}
                    deadline={ideasOpen ? ideaDeadlineEnd(deadline)!.toDate() : null}
                    people={hackathon.participants}
                    ideaCount={hackathon.ideas.filter(i => !i.archived).length}
                    teamPrompt={teamPrompt}
                    actions={actionItems.length > 0 ? actionItems.map(action => (
                        <Button key={action.path} variant="outlined" startIcon={action.icon} sx={{ minHeight: 40, backgroundColor: 'background.paper' }} onClick={() => navigate(`/h/${id}/${action.path}`)}>
                            {action.title}
                        </Button>
                    )) : undefined} />

                {
                    // let in to read a past event of their organization; say so, so nothing reads as broken
                    !!user && isReadOnlyViewer(hackathon, user.email) && <Alert severity="info">
                        A past hackathon{hackathon.organization ? <> at <b>{hackathon.organization.name}</b></> : null}. You weren't part of it,
                        so you can look around but not change anything.
                    </Alert>
                }

                <LiveNowStrip hackathonId={id} hackathon={hackathon} />

                <ResultsCard hackathon={hackathon} />

                {
                    // a member who opened a judge link: one tap to take it up
                    !!user && !isJudge && !!judgeInvite && <Alert severity="info" action={<Button color="inherit" size="small" disabled={acceptingJudge} onClick={() => {
                        setAcceptingJudge(true);
                        joinHackathon(id, '', judgeInvite).then(() => window.location.replace(`/h/${hackathon.slug}`), err => {
                            setJudgeInviteError(err.response?.data?.message || 'Could not accept the invitation');
                            setAcceptingJudge(false);
                        });
                    }}>Judge this hackathon</Button>}>
                        {judgeInviteError || <>You've been invited to <b>judge</b> {hackathon.name}.</>}
                    </Alert>
                }

                <ParticipantVotingNotice hackathonId={id} hackathon={hackathon} hidden={!user || isJudge || isOrganizer} />

                <Box sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr)' },
                    alignItems: 'start'
                }}>
                    <Card>
                        <CardContent sx={{ p: { xs: 2, md: 3 } }} className="marked">
                            {
                                hackathon.description
                                    ? <Markdown>{hackathon.description}</Markdown>
                                    : <Typography color="text.secondary">No description yet.</Typography>
                            }
                        </CardContent>
                    </Card>
                    <RulesCard hackathon={hackathon} isOrganizer={isOrganizer} />
                </Box>

                <SessionsCard hackathonId={id} hackathon={hackathon} isOrganizer={isOrganizer} />

                <IdeasList hackathon={live} reloadHackathon={() => reload && reload()} />

                {
                    !hackathon.votingPeriodOn && hackathon.ideas.length > 2 && <ScrollTopButton window={() => window} />
                }
            </Stack>
        </PageContainer>
    )
}
